import type { GlobalSearchItem, GlobalSearchKind, GlobalSearchSource } from "@etain/domain";

import { crmRecordPath, crmSalesPath, crmSectionPath } from "./crm-navigation.js";
import { projectPath } from "./project-navigation.js";

export function globalSearchQuery(query: string) {
  return new URLSearchParams({ q: query.trim() }).toString();
}

export function isRemoteSearchQuery(query: string): boolean {
  const text = query.trim();
  return text.length >= 2 && text.length <= 200 && !text.startsWith("/");
}

export function globalSearchAccessKey(access: {
  userId: string;
  tenantId: string;
  role: string;
  features: Record<string, boolean>;
}) {
  return JSON.stringify([
    access.userId,
    access.tenantId,
    access.role,
    Object.entries(access.features).sort(([left], [right]) => left.localeCompare(right)),
  ]);
}

const kindLabels: Record<GlobalSearchKind, string> = {
  organization: "Organisation",
  contact: "Kontakt",
  lead: "Interessent",
  deal: "Deal",
  offer: "Angebot",
  activity: "Aktivität",
  project: "Projekt",
  task: "Aufgabe",
  knowledge: "Wissen",
  file: "Datei",
  document: "CRM-Datei",
  audience: "Zielgruppe",
  phone_call: "Telefonnotiz",
  mail: "E-Mail",
  survey: "Befragung",
  survey_question: "Befragungsfrage",
  knowledge_admin: "Wissensfreigabe",
};

export function globalSearchResult(item: GlobalSearchItem, salesWorkspace: boolean) {
  const paths: Record<GlobalSearchKind, () => string> = {
    organization: () => crmRecordPath("organizations", item.id),
    contact: () => crmRecordPath("contacts", item.id),
    lead: () =>
      salesWorkspace
        ? crmSalesPath("lead", item.id)
        : `${crmSectionPath("leads")}/${encodeURIComponent(item.id)}`,
    deal: () =>
      salesWorkspace
        ? crmSalesPath("opportunity", item.id)
        : `${crmSectionPath("deals")}/${encodeURIComponent(item.id)}`,
    offer: () => crmRecordPath("offers", item.id),
    activity: () => crmRecordPath("activities", item.id),
    project: () => projectPath(item.id) ?? "",
    task: () => `/projekte?aufgabe=${encodeURIComponent(item.id)}`,
    knowledge: () => `/wissen/${encodeURIComponent(item.id)}`,
    file: () => `/dateiexplorer?fileId=${encodeURIComponent(item.id)}`,
    document: () => crmRecordPath("documents", item.id),
    knowledge_admin: () => `/administration?tab=knowledge&seite=${encodeURIComponent(item.id)}`,
    audience: () => `/crm/kontakte/zielgruppen?zielgruppe=${encodeURIComponent(item.id)}`,
    phone_call: () => `/crm/anrufe?anruf=${encodeURIComponent(item.id)}`,
    mail: () => `/crm/e-mail?nachricht=${encodeURIComponent(item.id)}`,
    survey: () => `/befragungen/${encodeURIComponent(item.id)}/results`,
    survey_question: () =>
      `/befragungen/${encodeURIComponent(item.id)}/results?frage=${item.questionId}`,
  };
  return {
    label: item.label,
    description: item.description,
    kind: kindLabels[item.kind],
    path: paths[item.kind](),
  };
}

export function isSavedSearchVisible(path: string, features: Record<string, boolean>) {
  const modulePaths = {
    crm: "/crm",
    projects: "/projekte",
    finance: "/finanzen",
    marketing: "/marketing",
    learning: "/lernen",
    surveys: "/befragungen",
    knowledge: "/wissen",
    reports: "/berichte",
    admin: "/administration",
  };
  const module = Object.entries(modulePaths).find(
    ([, prefix]) =>
      path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`),
  )?.[0];
  return !module || features[module] === true;
}

const sourceLabels: Record<GlobalSearchSource, string> = {
  crm: "CRM",
  projects: "Projekte",
  knowledge: "Wissensdatenbank",
  files: "Dateien",
  mail: "E-Mail-Eingang",
  surveys: "Befragungen",
  knowledge_admin: "Wissensfreigaben",
};

export function globalSearchStatus(state: {
  query: string;
  debouncing: boolean;
  fetching: boolean;
  error: boolean;
  failedSources: readonly GlobalSearchSource[];
  resultCount: number;
}): { message: string; canRetry: boolean } | null {
  const query = state.query.trim();
  if (!query) return null;
  if (query.startsWith("/"))
    return state.resultCount
      ? null
      : { message: "Keine passenden Aktionen gefunden.", canRetry: false };
  if (query.length < 2)
    return { message: "Geben Sie mindestens zwei Zeichen ein.", canRetry: false };
  if (query.length > 200)
    return { message: "Geben Sie höchstens 200 Zeichen ein.", canRetry: false };
  if (state.debouncing) return { message: "Suche wird vorbereitet …", canRetry: false };
  if (state.fetching) return { message: "Suche läuft …", canRetry: false };
  if (state.error) return { message: "Die Suche konnte nicht geladen werden.", canRetry: true };
  if (state.failedSources.length)
    return {
      message: `Ein Teil der Suche ist gerade nicht verfügbar: ${state.failedSources.map((source) => sourceLabels[source]).join(", ")}.`,
      canRetry: true,
    };
  return state.resultCount
    ? null
    : { message: "Keine passenden Treffer gefunden.", canRetry: false };
}
